// src/features/gamesReducer.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IconPrefix } from '@fortawesome/fontawesome-svg-core';

import { envBool } from "../utils/misc";

const isDevMode: boolean = envBool(import.meta.env.VITE_ENV_DEV_MODE);

export type UsageType = {
    /** id in db */
    usageID: string;
    /** name shown in ui */
    usageName: string;
    /** fontawesome icon prefix */
    usageIconPre: IconPrefix;
    /** fontawesome icon name */
    usageIconName: string;
    /** is defined */
    defined: string;
    /** color for each of the usages */
    usageColor: string;
}

export interface UIState {
    /** is on paid version */
    paidVersion: boolean;

    /** is ui hidden */
    hidden: boolean;

    /** color chosen by user for looks */
    schemeColor: string;

    /** set loading */
    isLoading: boolean;

    /** WebSocket connection status */
    socketConnected: boolean;

    /** used for hiding */
    slideClass: string;

    /** tab options */
    tabs: string[];
    /** current tab */
    activeTab: string;

    /** items for usage */
    usageItems: UsageType[];

    /** selected account for paying overdue fees */
    selectedAccount: string;

    /** selected roommate state id for perm editing */
    selectedRoommate: number;
}

const initialState: UIState = {
    paidVersion: false,
    hidden: !isDevMode,
    isLoading: false,
    socketConnected: false,
    schemeColor: "148, 96, 224", // 10, 255, 251

    slideClass: "slideUpPointFive",
    tabs: [
        "home",
        "upgrades",
        "maintenance",
        "access",
        //"rent",
        "settings",
    ],
    activeTab: "home",
    usageItems: [
        {
            usageID: "electric",
            usageName: "Electricity Usage",
            usageIconPre: "fas",
            usageIconName: "bolt-lightning",
            defined: "kWh",
            usageColor: "252, 214, 106",
        },
        {
            usageID: "water",
            usageName: "Water Usage",
            usageIconPre: "fas",
            usageIconName: "droplet-degree",
            defined: "%",
            usageColor: "94, 156, 255",
        },
        {
            usageID: "temperature",
            usageName: "Temperature",
            usageIconPre: "fas",
            usageIconName: "temperature-empty",
            defined: "°C",
            usageColor: "255, 185, 64",
        },
        // {
        //     usageID: "heating",
        //     usageName: "Heating Usage",
        //     usageIconPre: "fas",
        //     usageIconName: "heat",
        //     defined: "%",
        //     usageColor: "rgba(255, 103, 61, 1)",
        // },
        // {
        //     usageID: "gas",
        //     usageName: "Gas Usage",
        //     usageIconPre: "fas",
        //     usageIconName: "fire-flame-simple",
        //     defined: "%",
        //     usageColor: "rgba(26, 120, 24, 1)",
        // },
    ],

    selectedAccount: "",
    selectedRoommate: 0,
};

const uiReducer = createSlice({
    name: "uiReducer",
    initialState,
    reducers: {
        /**
         * Sets The Current Minigame to Show
         * @object {object} currentMinigame - the object that holds the minigame data
         */
      
        SET_activeTab: (state, action: PayloadAction<string>) => {
            //console.log("SETTING TAB INDEX", action.payload);
            state.activeTab = action.payload;
        },

        SET_hidden: (state, action: PayloadAction<boolean>) => {
            state.hidden = action.payload;
        },

        SET_schemeColor: (state, action: PayloadAction<string>) => {
            state.schemeColor = action.payload;
        },

        SET_isLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },

        SET_socketConnected: (state, action: PayloadAction<boolean>) => {
            state.socketConnected = action.payload;
        },

        SET_slideClass: (state, action: PayloadAction<string>) => {
            state.slideClass = action.payload;
        },

        SET_selectedAccount: (state, action: PayloadAction<string>) => {
            state.selectedAccount = action.payload;
        },

        SET_selectedRoommate: (state, action: PayloadAction<number>) => {
            state.selectedRoommate = action.payload;
        },

        SET_UIDATA: (state, action: PayloadAction<UIState>) => {
            //console.log("SETTING LAB DATA", action.payload);
            return {...state, ...action.payload};
        },
    },
});

export const { SET_activeTab, SET_hidden, SET_slideClass, SET_isLoading, SET_socketConnected, SET_selectedAccount, SET_selectedRoommate, SET_schemeColor, SET_UIDATA } = uiReducer.actions;
export default uiReducer.reducer;
